import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/http";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const addDaysStr = (iso, n) => { const [y, m, d] = iso.split("-").map(Number); const dt = new Date(y, m - 1, d + n); return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`; };
const JUMP_WEEKS = [2, 4, 6, 8, 12];

// Timely-style month grid for phones: tap a day to jump, dots show days with bookings.
export const MonthJumpSheet = ({ open, onOpenChange, date, onPick }) => {
  const [ym, setYm] = useState(date.slice(0, 7));
  const [counts, setCounts] = useState({});
  const [back, setBack] = useState(false);
  useEffect(() => { if (open) { setYm(date.slice(0, 7)); setBack(false); } }, [open, date]);
  useEffect(() => {
    if (!open) return;
    api.get("/bookings/day-counts", { params: { month: ym } }).then(({ data }) => setCounts(data.counts || {})).catch(() => setCounts({}));
  }, [open, ym]);
  const [y, m] = ym.split("-").map(Number);
  const cells = useMemo(() => {
    const first = new Date(y, m - 1, 1);
    const lead = (first.getDay() + 6) % 7; // Monday-first
    const days = new Date(y, m, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${y}-${pad(m)}-${pad(i + 1)}`)];
  }, [y, m]);
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; })();
  const shift = (n) => { const d = new Date(y, m - 1 + n, 1); setYm(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`); };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-6 pt-4" data-testid="month-jump-sheet">
        <SheetHeader className="mb-2">
          <div className="flex items-center justify-between">
            <button onClick={() => shift(-1)} className="h-10 w-10 rounded-lg flex items-center justify-center text-muted-foreground active:scale-90" data-testid="month-prev"><ChevronLeft className="h-5 w-5" /></button>
            <SheetTitle className="font-heading text-base" data-testid="month-title">{MONTHS[m - 1]} {y}</SheetTitle>
            <button onClick={() => shift(1)} className="h-10 w-10 rounded-lg flex items-center justify-center text-muted-foreground active:scale-90" data-testid="month-next"><ChevronRight className="h-5 w-5" /></button>
          </div>
        </SheetHeader>
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto -mx-4 px-4" data-testid="month-jump-weeks">
          <button onClick={() => setBack((v) => !v)} data-testid="month-jump-dir"
            className="shrink-0 h-8 px-2.5 rounded-full text-[11px] font-mono-label uppercase tracking-wider border border-border text-muted-foreground active:scale-95">
            {back ? "Jump back" : "Jump ahead"}
          </button>
          {JUMP_WEEKS.map((w) => (
            <button key={w} onClick={() => { onPick(addDaysStr(date, (back ? -w : w) * 7)); onOpenChange(false); }} data-testid={`month-jump-${back ? "back" : "fwd"}-${w}w`}
              className="shrink-0 h-8 px-3 rounded-full text-xs font-medium border border-border text-foreground bg-card active:scale-95 active:bg-primary active:text-primary-foreground">
              {back ? "\u2212" : "+"}{w} wks
            </button>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 text-center">
          {DOW.map((d) => <div key={d} className="text-[11px] font-medium text-muted-foreground py-1">{d}</div>)}
          {cells.map((d, i) => d ? (
            <button key={d} onClick={() => { onPick(d); onOpenChange(false); }} data-testid={`month-day-${d}`}
              className={`h-11 rounded-xl flex flex-col items-center justify-center text-sm transition-colors active:scale-95 ${d === date ? "bg-primary text-primary-foreground" : d === today ? "border border-primary text-primary" : "hover:bg-muted"}`}>
              <span className="leading-none">{Number(d.slice(8, 10))}</span>
              <span className={`mt-1 h-1.5 w-1.5 rounded-full ${counts[d] ? (d === date ? "bg-primary-foreground" : "bg-primary") : "bg-transparent"}`} data-testid={counts[d] ? `month-dot-${d}` : undefined} />
            </button>
          ) : <div key={`e${i}`} />)}
        </div>
        <button onClick={() => { onPick(today); onOpenChange(false); }} className="mt-3 w-full h-10 rounded-lg border border-border text-sm font-medium" data-testid="month-today">Jump to today</button>
      </SheetContent>
    </Sheet>
  );
};
